import { reverse } from "@reactivated";

import { IAddress } from "../models/address";
import { IOrderResponse, IPayment, IPaymentStates } from "../models/checkout";
import { CSRF_HEADER, ajax, getCSRFToken } from "../utils/ajax";
import { PromiseMutex } from "../utils/mutex";

export const listEnabledPaymentMethods = async (): Promise<string[]> => {
    const mutex = new PromiseMutex<string[]>("list-enabled-payment-methods");
    let loading = mutex.getPromise();
    if (!loading) {
        loading = ajax
            .get(reverse("api-checkout-payment-methods"))
            .set("Accept", "application/json")
            .then((resp) => {
                return Object.keys(resp.body);
            });
        mutex.setPromise(loading);
    }
    return loading;
};

export const saveEmailAddress = async (
    email: string,
): Promise<{ owner_email: string }> => {
    return ajax
        .post(reverse("api-basket-email"))
        .set("Accept", "application/json")
        .set(CSRF_HEADER, await getCSRFToken())
        .send({
            owner_email: email,
        })
        .then((resp): { owner_email: string } => {
            return resp.body;
        });
};

export const loadEmailAddress = async (): Promise<{ owner_email: string }> => {
    const mutex = new PromiseMutex<{ owner_email: string }>(
        "load-basket-email-address",
    );
    let loading = mutex.getPromise();
    if (!loading) {
        loading = ajax
            .get(reverse("api-basket-email"))
            .set("Accept", "application/json")
            .then((resp): { owner_email: string } => {
                return resp.body;
            });
        mutex.setPromise(loading);
    }
    return loading;
};

export const saveShippingAddress = async (
    shippingAddress: IAddress,
    bypassUSPSValidation = false,
): Promise<IAddress> => {
    const data = {
        ...shippingAddress,
        bypass_validation: bypassUSPSValidation ? "t" : "f",
    };
    return ajax
        .post(reverse("checkout-shipping-address"))
        .set("Accept", "application/json")
        .set(CSRF_HEADER, await getCSRFToken())
        .send(data)
        .then((resp): IAddress => {
            return resp.body;
        });
};

export const loadShippingAddress = async (): Promise<IAddress> => {
    const mutex = new PromiseMutex<IAddress>("load-shipping-address");
    let loading = mutex.getPromise();
    if (!loading) {
        loading = ajax
            .get(reverse("checkout-shipping-address"))
            .set("Accept", "application/json")
            .then((resp): IAddress => {
                return resp.body;
            });
        mutex.setPromise(loading);
    }
    return loading;
};

export const clearShippingAddress = async (): Promise<void> => {
    return ajax
        .delete(reverse("checkout-shipping-address"))
        .set("Accept", "application/json")
        .set(CSRF_HEADER, await getCSRFToken())
        .then((resp): void => {
            return resp.body;
        });
};

export const saveBillingAddress = async (
    billingAddress: IAddress,
    bypassUSPSValidation = false,
): Promise<IAddress> => {
    const data = {
        ...billingAddress,
        bypass_validation: bypassUSPSValidation ? "t" : "f",
    };
    return ajax
        .post(reverse("checkout-billing-address"))
        .set("Accept", "application/json")
        .set(CSRF_HEADER, await getCSRFToken())
        .send(data)
        .then((resp): IAddress => {
            return resp.body;
        });
};

export const loadBillingAddress = async (): Promise<IAddress> => {
    const mutex = new PromiseMutex<IAddress>("load-billing-address");
    let loading = mutex.getPromise();
    if (!loading) {
        loading = ajax
            .get(reverse("checkout-billing-address"))
            .set("Accept", "application/json")
            .then((resp): IAddress => {
                return resp.body;
            });
        mutex.setPromise(loading);
    }
    return loading;
};

export const clearBillingAddress = async (): Promise<void> => {
    return ajax
        .delete(reverse("checkout-billing-address"))
        .set("Accept", "application/json")
        .set(CSRF_HEADER, await getCSRFToken())
        .then((resp): void => {
            return resp.body;
        });
};

export const placeOrder = async (
    basketURL: string,
    shippingAddress: IAddress,
    shippingMethodCode: string,
    billingAddress: IAddress,
    payment: IPayment,
    guestEmail?: string,
    recaptcha?: string,
): Promise<IOrderResponse> => {
    const body = {
        guest_email: guestEmail,
        basket: basketURL,
        shipping_address: shippingAddress,
        shipping_method_code: shippingMethodCode,
        billing_address: billingAddress,
        payment: payment,
        recaptcha: recaptcha,
    };

    return ajax
        .post(reverse("api-checkout"))
        .set(CSRF_HEADER, await getCSRFToken())
        .set("Accept", "application/json")
        .send(body)
        .then((resp): IOrderResponse => {
            return resp.body;
        });
};

export const completeDeferredPayment = async (
    basketToken: string,
    orderToken: string,
    payment: IPayment,
): Promise<IOrderResponse> => {
    const body = {
        order: orderToken,
        payment: payment,
    };
    return ajax
        .post(reverse("api-complete-deferred-payment"))
        .set(CSRF_HEADER, await getCSRFToken())
        .set("Accept", "application/json")
        .query({
            "basket-token": basketToken,
        })
        .send(body)
        .then((resp): IOrderResponse => {
            return resp.body;
        });
};

export const checkPaymentStates = async (): Promise<IPaymentStates> => {
    // TODO: Use reverse here once url name is fixed. rreverse("api-payment-states-list")
    return ajax
        .get("/api/checkout/payment-states/")
        .set("Accept", "application/json")
        .then((resp): IPaymentStates => {
            return resp.body;
        });
};
